import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { BeheerSection } from '../../components/beheer/BeheerSection';
import { PersonaPanel } from '../../components/beheer/anna/PersonaPanel';
import { VoicesPanel } from '../../components/beheer/anna/VoicesPanel';
import { HouseModesTable } from '../../components/beheer/anna/HouseModesTable';
import { InitiativePanel } from '../../components/beheer/anna/InitiativePanel';
import { AnnaAvatar } from '../../components/avatar/AnnaAvatar';
import { useDb } from '../../hooks/useDb';
import { db } from '../../utils/db';

/**
 * Who Anna is. Changes apply at once to every screen (the wall's sounds follow the house modes,
 * every spoken line uses the chosen voice); one quiet log row per burst of edits.
 */
export function AnnaSettings() {
  const settings = useDb(t => t.settings);
  const [saved, setSaved] = useState(false);
  const last = useRef(settings);
  useEffect(() => {
    if (last.current === settings) return;
    last.current = settings;
    setSaved(false);
    const id = window.setTimeout(() => {
      setSaved(true);
      db.log({
        agent: 'systeem',
        summary: 'Instellingen van Anna bijgewerkt',
        level: 'zelf',
        where: 'lokaal',
        outcome: 'uitgevoerd'
      });
    }, 700);
    return () => window.clearTimeout(id);
  }, [settings]);
  return <BeheerLayout title="Anna" intro="Wie Anna is: hoe ze praat, hoe ze klinkt, wat elke huismodus doet en hoeveel ze zelf onderneemt." aside={<div className="flex items-center gap-4" data-id="element-1824" data-mp="9rdbumyweran">
          {saved && <span className="flex items-center gap-1.5 text-[13px] text-ink-soft" role="status" data-id="element-1825" data-mp="9tgafve49sqz">
              <CheckIcon className="h-3.5 w-3.5 text-accent" aria-hidden data-id="element-1826" data-mp="4yk6pxzipgy1" /> Opgeslagen, geldt op elk scherm
            </span>}
          <div className="aspect-square w-20" aria-hidden data-id="element-1827" data-mp="38hnbg1p2uizh">
            <AnnaAvatar state="rust" data-id="element-1828" data-mp="38h3rygjs3mjc" />
          </div>
        </div>} data-id="element-1823" data-mp="2lo6r0vet5j">
      <BeheerSection id="persona" title="Persoonlijkheid" className="mt-10" data-id="element-1829" data-mp="1cdfou1xm3e3q">
        <PersonaPanel persona={settings.persona} onChange={persona => db.updateSettings({
        persona
      })} data-id="element-1830" data-mp="1hck3h83b241g" />
      </BeheerSection>

      <div className="mt-16 grid gap-10 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" data-id="element-1831" data-mp="1m4k6j9eo9d29">
        <BeheerSection id="stemmen" title="Stem" description="Lokale stemmen draaien op Piper; de cloudstem kost per zin." data-id="element-1832" data-mp="1r1tuslqbrioi">
          <VoicesPanel selected={settings.voiceId} onSelect={voiceId => db.updateSettings({
          voiceId
        })} data-id="element-1833" data-mp="thhbwsd49mha" />
        </BeheerSection>
        <BeheerSection id="initiatief" title="Initiatief" description="Elke kandidaat krijgt een score. Alleen boven de drempel doet of vraagt Anna iets." data-id="element-1834" data-mp="13dsnth8soab7">
          <InitiativePanel value={settings.initiative} onChange={initiative => db.updateSettings({
          initiative
        })} data-id="element-1835" data-mp="17ggyzvkzdtbc" />
        </BeheerSection>
      </div>

      <BeheerSection id="modi" title="Huismodi" description="Veiligheidsmeldingen komen in elke modus door. Geluid hier bepaalt meteen wat de wand en het bureau laten horen." className="mt-16" data-id="element-1836" data-mp="2itrrogbdpk5n">
        <HouseModesTable modes={settings.houseModes} onChange={next => db.updateSettings({
        houseModes: settings.houseModes.map(m => m.mode === next.mode ? next : m)
      })} data-id="element-1837" data-mp="34udtbrhqdl2" />
      </BeheerSection>
    </BeheerLayout>;
}